import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ActivityRow, RequestDetails, useAccordion } from "@/components/work/Activity";
import { KeyRound, Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { PageHeader } from "@/components/PageHeader";
import { ApprovalStatus, STATUS_OPTIONS } from "@/components/approvals/ApprovalBits";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const viewsFor = (me) => [
  me.can_approve && ["pending", "Pending"],
  me.can_recommend && ["recommend", "To recommend"],
  me.can_submit && ["mine", "My requests"],
  ["all", "All requests"],
].filter(Boolean);

const Row = ({ r, open, onToggle }) => (
  <ActivityRow testid={`approval-row-${r.id}`} type={r.last_event?.type || "submitted"} label={r.last_event?.label || "Submitted"} title={`${r.number} · ${r.title}`}
    at={r.last_event?.at || r.updated_at} by={r.last_event?.by || r.applicant.name} amount={r.amount} code={r.status === "approved" ? r.approval_code : null}
    pill={<ApprovalStatus r={r} testid={`approval-status-${r.id}`} />} statusLabel={r.status_label} to={`/approvals/${r.id}`} open={open} onToggle={onToggle}>
    <RequestDetails r={r} wide />
  </ActivityRow>
);

const FindByCode = () => {
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const find = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit approval code"); return; }
    try { const { data } = await api.get(`/approvals/by-code/${code}`); navigate(`/approvals/${data.id}`); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={find} className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-2" data-testid="find-by-code-form">
      <KeyRound className="ml-1 h-4 w-4 shrink-0 text-emerald-700" />
      <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="Approval code" className="h-9 min-w-0 flex-1 bg-white font-mono tracking-widest sm:w-48 sm:flex-none" data-testid="find-by-code-input" />
      <Button type="submit" size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="find-by-code-button">Open</Button>
    </form>
  );
};

export default function ApprovalsPage() {
  const [meta, setMeta] = useState(null);
  const [sp, setSp] = useSearchParams();
  const [list, setList] = useState(null);
  const [open, toggle] = useAccordion();
  const views = meta ? viewsFor(meta.me) : [];
  const view = sp.get("view") || views[0]?.[0];
  const f = Object.fromEntries(["status", "category", "q", "date_from", "date_to", "amount_min", "amount_max"].map((k) => [k, sp.get(k) || ""]));
  const setQ = (p) => setSp((prev) => { const n = new URLSearchParams(prev); Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n; }, { replace: true });

  useEffect(() => { api.get("/approvals/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const key = sp.toString();
  const load = useCallback(() => {
    if (!view) return;
    const params = { view, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    api.get("/approvals", { params }).then((r) => setList(r.data.requests)).catch((e) => toast.error(apiError(e)));
  }, [view, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("task-updated", load); return () => window.removeEventListener("task-updated", load); }, [load]);

  if (!meta) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-5" data-testid="approvals-page">
      <PageHeader brand eyebrow="Work" title="Approvals" subtitle="Financial requests: applicant → Division Head recommendation → MD approval."
        actions={meta.me.can_submit && <Button asChild className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="new-approval-button"><Link to="/approvals/new"><Plus className="h-4 w-4" /> New request</Link></Button>} />
      {meta.me.can_find_code && <FindByCode />}
      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="approval-views">
        {views.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setQ({ view: k })} className={cn("rounded-md px-3 py-2 text-sm font-medium", view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")} data-testid={`approval-view-${k}`}>{label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center" data-testid="approval-filters">
        <div className="relative col-span-2 sm:col-span-1"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input defaultValue={f.q} placeholder="Search title, code, payee" className="h-9 w-full bg-white pl-8 sm:w-64" onKeyDown={(e) => e.key === "Enter" && setQ({ q: e.currentTarget.value })} onBlur={(e) => setQ({ q: e.target.value })} data-testid="approval-search-input" /></div>
        <Select value={f.status || "all"} onValueChange={(v) => setQ({ status: v === "all" ? "" : v })}>
          <SelectTrigger className="h-9 w-full bg-white sm:w-52" data-testid="approval-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUS_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.category || "all"} onValueChange={(v) => setQ({ category: v === "all" ? "" : v })}>
          <SelectTrigger className="h-9 w-full bg-white sm:w-40" data-testid="approval-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <DateInput value={f.date_from} clearable onChange={(x) => setQ({ date_from: x })} className="h-9 w-full sm:w-36" testid="approval-from-filter" />
        <span className="hidden text-xs text-slate-400 sm:inline">to</span>
        <DateInput value={f.date_to} clearable onChange={(x) => setQ({ date_to: x })} className="h-9 w-full sm:w-36" testid="approval-to-filter" />
        <Input inputMode="decimal" defaultValue={f.amount_min} placeholder="Min amount" className="h-9 w-full bg-white sm:w-28" onBlur={(e) => setQ({ amount_min: e.target.value })} data-testid="approval-amount-min" />
        <Input inputMode="decimal" defaultValue={f.amount_max} placeholder="Max amount" className="h-9 w-full bg-white sm:w-28" onBlur={(e) => setQ({ amount_max: e.target.value })} data-testid="approval-amount-max" />
      </div>
      <div className="space-y-2.5" data-testid="approvals-list">
        {!list && <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        {list && list.length === 0 && <p className="rounded-2xl bg-white px-4 py-10 text-center text-sm text-slate-500 ring-1 ring-slate-200" data-testid="approvals-empty">No requests here</p>}
        {list?.map((r) => <Row key={r.id} r={r} open={open.has(r.id)} onToggle={() => toggle(r.id)} />)}
      </div>
    </div>
  );
}
